import React, { useState, useEffect } from 'react';
import axios from '../api/client';

export default function Approvals() {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState('');
  const [signatureData, setSignatureData] = useState('');

  // Mock user ID - in production, get from auth context
  const currentUserId = 'user-1';

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`/approvals/pending?approver_id=${currentUserId}`);
      setPendingApprovals(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setLoading(false);
    }
  };

  const handleApprove = async (approvalId) => {
    try {
      await axios.post(`/approvals/${approvalId}/approve`, {
        comments,
        signature_data: signatureData,
        ip_address: window.location.hostname
      });
      alert('Approved successfully!');
      fetchPendingApprovals();
      setSelectedApproval(null);
      setComments('');
      setSignatureData('');
    } catch (error) {
      console.error('Error approving:', error);
      alert('Error approving');
    }
  };

  const handleReject = async (approvalId) => {
    if (!comments) {
      alert('Please provide comments for rejection');
      return;
    }
    try {
      await axios.post(`/approvals/${approvalId}/reject`, {
        comments,
        ip_address: window.location.hostname
      });
      alert('Rejected successfully');
      fetchPendingApprovals();
      setSelectedApproval(null);
      setComments('');
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Error rejecting');
    }
  };

  const getStatusColor = (daysOverdue) => {
    if (daysOverdue > 0) return 'text-red-600';
    return 'text-yellow-600';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">Loading approvals...</div>
      </div>
    );
  }

  if (selectedApproval) {
    const step = selectedApproval.pending_step;
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6 border-b border-gray-200">
            <button
              onClick={() => setSelectedApproval(null)}
              className="text-blue-600 hover:text-blue-800 mb-4"
            >
              ← Back to Approvals
            </button>
            <h2 className="text-2xl font-bold">Approval Required</h2>
            <p className="text-gray-600 mt-2">
              {selectedApproval.record_type} - {selectedApproval.record_id}
            </p>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Workflow Progress</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  Step {selectedApproval.current_step} of {selectedApproval.current_step}
                </span>
                <div className="flex-1 bg-gray-200 h-2 rounded-full">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${(selectedApproval.current_step / selectedApproval.current_step) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold mb-2">Details</h3>
              <div className="bg-gray-50 p-4 rounded">
                <p><span className="font-medium">Submitted by:</span> {selectedApproval.submitted_by}</p>
                <p><span className="font-medium">Submitted on:</span> {formatDate(selectedApproval.submitted_date)}</p>
                <p><span className="font-medium">Status:</span> {selectedApproval.status}</p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Comments *</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows="4"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your comments..."
                required
              />
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Digital Signature (Optional)</label>
              <input
                type="text"
                value={signatureData}
                onChange={(e) => setSignatureData(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Type your name to sign"
              />
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => handleApprove(step.id)}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold"
              >
                ✓ Approve
              </button>
              <button
                onClick={() => handleReject(step.id)}
                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold"
              >
                ✗ Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Pending My Approval</h1>
        <p className="text-gray-600 mt-2">
          {pendingApprovals.length} approval{pendingApprovals.length !== 1 ? 's' : ''} pending
        </p>
      </div>

      {pendingApprovals.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No pending approvals at this time.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingApprovals.map((approval) => {
            const step = approval.pending_step;
            return (
              <div
                key={approval.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                        {approval.record_type}
                      </span>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                        Due Today
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">
                      {approval.record_type} - {approval.record_id}
                    </h3>
                    <p className="text-gray-600 mb-2">
                      Submitted by {approval.submitted_by} on {formatDate(approval.submitted_date)}
                    </p>
                    <p className="text-gray-600">
                      Step {approval.current_step} - Priority: {approval.record_type === 'capa' ? 'High' : 'Medium'}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedApproval(approval)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    Review & Approve →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
